import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from 'stores'
import {
  IAthleteProfile,
  ICoachProfile,
  getMyProfiles,
  createChildAthleteProfile,
  createCoachProfile as apiCreateCoachProfile
} from 'API/profile'
import { ActiveProfileType, AuthenticateParams } from 'configs/constants/enums/auth'

export interface ActiveProfileSelection {
  id: number
  type: ActiveProfileType
}

/**
 * Active-profile state for the profile-centric model. The store holds the
 * user's pick (athlete vs coach profile); the API/index.ts axios interceptor
 * reads it via localStorage and stamps the X-Active-Profile-* headers on
 * every outbound request. Persisted to localStorage so the choice survives
 * page reloads; cleared on logout.
 *
 * NOTE: localStorage values are a HINT only — the backend still authorizes
 * every request against the user's actual ownership of the referenced profile.
 */
export default class ProfileStore {
  rootStore: RootStore

  athleteProfiles: IAthleteProfile[] = []
  coachProfile: ICoachProfile | null = null
  activeProfile: ActiveProfileSelection | null = null
  isLoading = false
  /** True once loadMyProfiles has settled at least once, so pages can tell "not loaded yet" from "no profile". */
  hasLoaded = false
  error: string | null = null

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
    this.hydrateFromStorage()
  }

  private hydrateFromStorage(): void {
    const rawId = localStorage.getItem(AuthenticateParams.ACTIVE_PROFILE_ID)
    const rawType = localStorage.getItem(AuthenticateParams.ACTIVE_PROFILE_TYPE)
    if (!rawId || !rawType) return
    const id = Number.parseInt(rawId, 10)
    if (!Number.isFinite(id) || id <= 0) return
    if (rawType !== ActiveProfileType.ATHLETE && rawType !== ActiveProfileType.COACH) return
    this.activeProfile = { id, type: rawType as ActiveProfileType }
  }

  setActiveProfile(selection: ActiveProfileSelection | null): void {
    this.activeProfile = selection
    if (selection) {
      localStorage.setItem(AuthenticateParams.ACTIVE_PROFILE_ID, String(selection.id))
      localStorage.setItem(AuthenticateParams.ACTIVE_PROFILE_TYPE, selection.type)
    } else {
      localStorage.removeItem(AuthenticateParams.ACTIVE_PROFILE_ID)
      localStorage.removeItem(AuthenticateParams.ACTIVE_PROFILE_TYPE)
    }
  }

  /**
   * Insert-or-replace an athlete profile in the local cache (id-keyed). Lets a
   * freshly created profile appear in `profiles` immediately — before the next
   * loadMyProfiles refetch — so the dashboard doesn't briefly fall back to
   * profiles[0] (the player) right after creating a child.
   */
  upsertAthleteProfile(profile: IAthleteProfile): void {
    const exists = this.athleteProfiles.some(p => p.id === profile.id)
    this.athleteProfiles = exists
      ? this.athleteProfiles.map(p => (p.id === profile.id ? profile : p))
      : [...this.athleteProfiles, profile]
  }

  /** Replace the cached coach profile (same eager-cache rationale as upsertAthleteProfile). */
  setCoachProfile(profile: ICoachProfile): void {
    this.coachProfile = profile
  }

  /**
   * Fetch the caller's full profile bundle and auto-pick a default active
   * profile when exactly one option exists. With more than one, the caller
   * navigates to the profile picker UI and we leave activeProfile untouched.
   */
  async loadMyProfiles(): Promise<void> {
    runInAction(() => {
      this.isLoading = true
      this.error = null
    })
    try {
      const bundle = await getMyProfiles()
      runInAction(() => {
        this.athleteProfiles = bundle.athleteProfiles
        this.coachProfile = bundle.coachProfile
      })

      // Auto-select sensible default ONLY when caller has no prior choice and
      // there is exactly one candidate. Otherwise the UI prompts the user.
      const total = this.athleteProfiles.length + (this.coachProfile ? 1 : 0)
      if (!this.activeProfile && total === 1) {
        if (this.athleteProfiles.length === 1) {
          this.setActiveProfile({
            id: this.athleteProfiles[0].id,
            type: ActiveProfileType.ATHLETE
          })
        } else if (this.coachProfile) {
          this.setActiveProfile({
            id: this.coachProfile.id,
            type: ActiveProfileType.COACH
          })
        }
      }

      // If the stored active profile is no longer in the bundle (claim, delete,
      // server-side rotation), drop it so subsequent reads start fresh.
      if (this.activeProfile) {
        const stillExists =
          (this.activeProfile.type === ActiveProfileType.ATHLETE &&
            this.athleteProfiles.some(p => p.id === this.activeProfile?.id)) ||
          (this.activeProfile.type === ActiveProfileType.COACH &&
            this.coachProfile?.id === this.activeProfile.id)
        if (!stillExists) {
          this.setActiveProfile(null)
        }
      }
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load profiles'
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
        this.hasLoaded = true
      })
    }
  }

  async addChildAthleteProfile(patch: Partial<IAthleteProfile>): Promise<IAthleteProfile | null> {
    try {
      const child = await createChildAthleteProfile(patch)
      runInAction(() => {
        this.athleteProfiles = [...this.athleteProfiles, child]
      })
      return child
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to create child profile'
      })
      return null
    }
  }

  async ensureCoachProfile(displayName?: string): Promise<ICoachProfile | null> {
    try {
      const coach = await apiCreateCoachProfile(displayName)
      runInAction(() => {
        this.coachProfile = coach
      })
      return coach
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to ensure coach profile'
      })
      return null
    }
  }

  reset(): void {
    this.athleteProfiles = []
    this.coachProfile = null
    this.setActiveProfile(null)
    this.isLoading = false
    this.hasLoaded = false
    this.error = null
  }
}
